import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faPhone,
  faCalendar,
  faClock,
  faUsers,
  faMapMarkerAlt,
  faCommentAlt,
  faBox,
  faShoppingCart,
} from "@fortawesome/pro-light-svg-icons";
import Modal from "../../../utils/Modal";
import Button from "../../../buttons/Button";

const BookingDetailModal = ({
  item,
  tables = [],
  isOpen,
  onClose,
  onCancel,
  tKey = "dashboard.bookings.detail.",
}) => {
  const { t } = useTranslation();

  if (!item) return null;

  const isOrder = item.type === "order";

  const assignedTable = tables
    .flatMap((r) => r.tables || [])
    .find((tbl) =>
      item.tableBookings?.some((tb) => tb.tableId === tbl.tableId),
    );

  const itemDate = new Date(item.bookingDate);
  const canCancel =
    !isOrder && (item.status === "confirmed" || item.status === "pending");

  const getStatusKey = (status) => {
    const map = {
      pending: "pending",
      confirmed: "confirmed",
      declined: "declined",
      canceled: "canceled",
      no_show: "noShow",
      completed: "completed",
    };
    return map[status] || status;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} noFooter>
      <div
        className={`booking-detail ${isOrder ? "booking-detail--order" : ""}`}
      >
        {/* Header */}
        <div className="booking-detail__header">
          <div>
            <h3 className="booking-detail__guest-name">{item.guestName}</h3>
            <div className="booking-detail__header-badges">
              {isOrder && (
                <span className="booking-detail__type-badge booking-detail__type-badge--order">
                  <FontAwesomeIcon icon={faShoppingCart} />
                  <span>{t(`${tKey}order`)}</span>
                </span>
              )}
              <span className={`booking-status booking-status--${item.status}`}>
                {t(`dashboard.bookings.status.${getStatusKey(item.status)}`)}
              </span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="booking-detail__body">
          {/* Contact Info */}
          <div className="booking-detail__grid">
            {item.guestEmail && (
              <div className="booking-detail__field">
                <FontAwesomeIcon icon={faEnvelope} className="fa-icon" />
                <span>{item.guestEmail}</span>
              </div>
            )}
            {item.guestPhone && (
              <div className="booking-detail__field">
                <FontAwesomeIcon icon={faPhone} className="fa-icon" />
                <span>{item.guestPhone}</span>
              </div>
            )}
          </div>

          {/* Date/Time Details */}
          <div className="booking-detail__grid">
            <div className="booking-detail__field">
              <FontAwesomeIcon icon={faCalendar} className="fa-icon" />
              <span>
                {itemDate.toLocaleDateString("fr-FR", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </span>
            </div>
            <div className="booking-detail__field">
              <FontAwesomeIcon icon={faClock} className="fa-icon" />
              <span>
                {itemDate.toLocaleTimeString("fr-FR", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <div className="booking-detail__field">
              <FontAwesomeIcon icon={faUsers} className="fa-icon" />
              <span>
                {item.partySize} {t(`${tKey}guests`)}
              </span>
            </div>
            {assignedTable && (
              <div className="booking-detail__field">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="fa-icon" />
                <span>
                  {assignedTable.name}
                  {assignedTable.room ? ` (${assignedTable.room.name})` : ""}
                </span>
              </div>
            )}
          </div>

          {/* Order-specific info */}
          {isOrder && item.totalPrice != null && (
            <div className="booking-detail__grid">
              <div className="booking-detail__field">
                <FontAwesomeIcon icon={faBox} className="fa-icon" />
                <span>
                  {t(`${tKey}orderTotal`)}:{" "}
                  <strong>CHF {Number(item.totalPrice).toFixed(2)}</strong>
                </span>
              </div>
            </div>
          )}

          {/* Notes */}
          {item.notes && (
            <div className="booking-detail__section booking-detail__section--notes">
              <FontAwesomeIcon icon={faCommentAlt} className="fa-icon mr-1" />
              <div>
                <div className="booking-detail__section-title">
                  {t(`${tKey}specialRequests`)}
                </div>
                <span>{item.notes}</span>
              </div>
            </div>
          )}

          {/* Staff Notes */}
          {item.staffNotes && (
            <div className="booking-detail__section booking-detail__section--notes">
              <div className="booking-detail__section-title">
                {t(`${tKey}staffNotes`)}
              </div>
              <span>{item.staffNotes}</span>
            </div>
          )}

          {/* Booking/Order Code */}
          {item.bookingCode && (
            <div className="booking-detail__field mb-2">
              <span className="booking-detail__code">
                {isOrder ? t(`${tKey}orderCode`) : t(`${tKey}bookingCode`)}:{" "}
                <strong>{item.bookingCode}</strong>
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="booking-detail__footer">
          {canCancel && onCancel && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => onCancel(item.bookingId)}
            >
              {t(`${tKey}cancelBooking`)}
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={onClose}>
            {t("utils.common.close")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default BookingDetailModal;
